// src/pages/PermanentGuestListPage.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ArrowLeftIcon,
  MagnifyingGlassIcon,
  StarIcon,  
  TagIcon,
  TicketIcon,
  MusicalNoteIcon, // Добавлено для ARTIST
  UserGroupIcon     // Добавлено для STAFF
} from "@heroicons/react/24/solid";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import AddPermanentGuestModalForList from "../components/AddPermanentGuestModalForList";
import EditPermanentGuestModalForList from "../components/EditPermanentGuestModalForList";
import ConfirmModal from "../components/ConfirmModal";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import { SERVER_BASE_URL } from "../config"; // Импорт централизованного URL
import Spinner from "../components/Spinner";

function PermanentGuestListPage() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [permanentGuests, setPermanentGuests] = useState([]);
  const [guestToEdit, setGuestToEdit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [guestToDelete, setGuestToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [listName, setListName] = useState("");
  const [listDetails, setListDetails] = useState(null);

  // Состояние для отображения гостей
  const [showGuestList, setShowGuestList] = useState(true);

  // Sorting states
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");

  // Bulk selection for guests
  const [selectedGuestIds, setSelectedGuestIds] = useState([]);
  const [showBulkConfirmModalGuests, setShowBulkConfirmModalGuests] = useState(false);

  // State for Via filtering
  const [viaFilter, setViaFilter] = useState(null);
  const [showViaDropdown, setShowViaDropdown] = useState(false);
  
  // State for Category filtering
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  
  // Refs for dropdown menus
  const viaDropdownRef = useRef(null);
  const categoryDropdownRef = useRef(null);

  const { list_id: permanentListId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Функция для отображения иконки категории гостя
  const renderCategoryIcon = (category) => {
    switch (category) {
      case "VIP":
        return <StarIcon className="h-4 w-4 text-yellow-500" title="VIP" />;
      case "BACKSTAGE":
        return <TagIcon className="h-4 w-4 text-purple-500" title="Backstage" />;
      case "ARTIST":
        return <MusicalNoteIcon className="h-4 w-4 text-purple-500" title="Artist" />;
      case "STAFF":
        return <UserGroupIcon className="h-4 w-4 text-purple-500" title="Staff" />;
      case "REGULAR":
      default:
        return <TicketIcon className="h-4 w-4 text-gray-400" title="Regular" />;
    }
  };

  // Функция для определения, мобильное ли устройство
  const isMobile = () => {
    return window.innerWidth <= 768;
  };

  // Hook to detect clicks outside of an element
  const useOutsideClick = (ref, callback) => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        callback();
      }
    };

    React.useEffect(() => {
      document.addEventListener("mousedown", handleClick);
      return () => {
        document.removeEventListener("mousedown", handleClick);
      };
    });
  };

  // Close dropdowns when clicking outside
  useOutsideClick(viaDropdownRef, () => setShowViaDropdown(false));
  useOutsideClick(categoryDropdownRef, () => setShowCategoryDropdown(false));

  // Function to get unique Via values
  const getUniqueViaValues = () => {
    const viaValues = permanentGuests
      .map(guest => guest.via || "N/A")
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort();
    return viaValues;
  };

  // Function to get unique Category values
  const getUniqueCategoryValues = () => {
    const categoryValues = permanentGuests
      .map(guest => guest.category || "REGULAR")
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort();
    return categoryValues;
  };

  // Function to clear Via filter
  const clearViaFilter = () => {
    setViaFilter(null);
    setShowViaDropdown(false);
  };

  // Function to clear Category filter
  const clearCategoryFilter = () => {
    setCategoryFilter(null);
    setShowCategoryDropdown(false);
  };

  const handleAddGuestList = () => setShowAddModal(true);
  const handleCloseModal = () => setShowAddModal(false);
  const handleEditGuest = (guest) => {
    setGuestToEdit(guest);
    setShowEditModal(true);
  };
  const handleCloseEditModal = () => setShowEditModal(false);

  const fetchPermanentGuests = useCallback(async () => {
    try {
      const response = await fetch(
        `${SERVER_BASE_URL}/api/permanent-guest-lists/${permanentListId}/guests/`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to load permanent guests list");
      }

      const data = await response.json();
      setPermanentGuests(data);
    } catch (error) {
      console.error("Error loading permanent guests:", error);
      toast.error("Failed to load permanent guests list.");
    } finally {
      setLoading(false);
    }
  }, [permanentListId]);

  const fetchPermanentGuestListDetail = useCallback(async () => {
    try {
      const response = await fetch(
        `${SERVER_BASE_URL}/api/permanent-guest-lists/${permanentListId}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to load permanent guest list details");
      }

      const data = await response.json();
      setListName(data.name);
      setListDetails(data);
    } catch (error) {
      console.error("Error loading permanent guest list details:", error);
      toast.error("Failed to load permanent guest list details.");
    }
  }, [permanentListId]);

  useEffect(() => {
    fetchPermanentGuests();
    fetchPermanentGuestListDetail();
  }, [fetchPermanentGuests, fetchPermanentGuestListDetail]);

  const handleCreateGuest = async (guestData) => {
    try {
      const response = await fetch(
        `${SERVER_BASE_URL}/api/permanent-guest-lists/${permanentListId}/guests/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(guestData),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to create permanent guest in list");
      }

      handleCloseModal();
      fetchPermanentGuests();
      toast.success("Permanent guest successfully added.");
    } catch (error) {
      console.error("Failed to create permanent guest:", error);
      toast.error("Failed to create permanent guest in list.");
    }
  };

  const handleUpdateGuest = async (updatedGuestData) => {
    try {
      const response = await fetch(
        `${SERVER_BASE_URL}/api/permanent-guest-lists/${permanentListId}/guests/${updatedGuestData.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            name: updatedGuestData.name,
            via: updatedGuestData.via,
            admission_type: updatedGuestData.admissionType,
            plus: updatedGuestData.plus,
            notes: updatedGuestData.notes,
            category: updatedGuestData.category
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update permanent guest");
      }

      fetchPermanentGuests();
      setShowEditModal(false);
      setGuestToEdit(null);
      toast.success("Permanent guest updated successfully.");
    } catch (error) {
      console.error("Failed to update permanent guest:", error);
      toast.error("Failed to update permanent guest.");
    }
  };

  const handleDeleteGuest = async () => {
    try {
      const response = await fetch(
        `${SERVER_BASE_URL}/api/permanent-guest-lists/${permanentListId}/guests/${guestToDelete}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete permanent guest");
      }

      fetchPermanentGuests();
      setShowConfirmModal(false);
      setGuestToDelete(null);
      toast.success("Permanent guest successfully deleted.");
    } catch (error) {
      console.error("Error deleting permanent guest:", error);
      toast.error("Failed to delete permanent guest.");
    }
  };

  const handleDeleteClick = (guestId) => {
    setGuestToDelete(guestId);
    setShowConfirmModal(true);
  };

  // Фильтрация гостей с учетом поисковой строки, фильтра Via и фильтра Category
  const filteredGuests = [...permanentGuests].filter((guest) => {
    const query = searchQuery.toLowerCase();
    const matchesSearch = 
      guest.name.toLowerCase().includes(query) ||
      (guest.via && guest.via.toLowerCase().includes(query)) ||
      (guest.admission_type && guest.admission_type.toLowerCase().includes(query)) ||
      (guest.category && guest.category.toLowerCase().includes(query));
    
    // Применяем фильтры Via и Category
    const matchesViaFilter = !viaFilter || guest.via === viaFilter;
    const matchesCategoryFilter = !categoryFilter || guest.category === categoryFilter;
    
    return matchesSearch && matchesViaFilter && matchesCategoryFilter;
  });

  // === Sorting ===
  const handleSort = (criteria) => {
    if (criteria === sortBy) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(criteria);
      setSortOrder("asc");
    }
  };

  const renderSortIcon = (column) => {
    if (column !== sortBy) {
      return <span className="text-gray-400">–</span>;
    }
    return sortOrder === "asc" ? (
      <span className="text-gray-600" style={{ fontSize: "11px" }}>
        ▲
      </span>
    ) : (
      <span className="text-gray-600" style={{ fontSize: "11px" }}>
        ▼
      </span>
    );
  };

  const sortedGuests = [...filteredGuests].sort((a, b) => {
    if (sortBy === "name") {
      return sortOrder === "asc"
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name);
    }
    if (sortBy === "via") {
      return sortOrder === "asc"
        ? (a.via || "").localeCompare(b.via || "")
        : (b.via || "").localeCompare(a.via || "");
    }
    if (sortBy === "admission_type") {
      return sortOrder === "asc"
        ? (a.admission_type || "").localeCompare(b.admission_type || "")
        : (b.admission_type || "").localeCompare(a.admission_type || "");
    }
    if (sortBy === "plus") {
      return sortOrder === "asc" ? a.plus - b.plus : b.plus - a.plus;
    }
    if (sortBy === "category") {
      return sortOrder === "asc"
        ? (a.category || "REGULAR").localeCompare(b.category || "REGULAR")
        : (b.category || "REGULAR").localeCompare(a.category || "REGULAR");
    }
    return 0;
  });

  // Bulk selection for guests
  const handleCheckboxChangeGuest = (guestId) => {
    setSelectedGuestIds((prev) =>
      prev.includes(guestId) ? prev.filter((id) => id !== guestId) : [...prev, guestId]
    );
  };

  const handleSelectAllGuests = () => {
    if (selectedGuestIds.length === sortedGuests.length) {
      setSelectedGuestIds([]);
    } else {
      setSelectedGuestIds(sortedGuests.map((guest) => guest.id));
    }
  };

  const handleDeleteSelectedGuests = async () => {
    try {
      await Promise.all(
        selectedGuestIds.map((id) =>
          fetch(`${SERVER_BASE_URL}/api/permanent-guest-lists/${permanentListId}/guests/${id}`, {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          })
        )
      );
      fetchPermanentGuests();
      setSelectedGuestIds([]);
      setShowBulkConfirmModalGuests(false);
      toast.success("Selected permanent guests successfully deleted.");
    } catch (error) {
      console.error("Error deleting selected permanent guests:", error);
      toast.error("Failed to delete selected permanent guests.");
    }
  };

  const confirmBulkDeleteGuests = async () => {
    await handleDeleteSelectedGuests();
  };

  return (
    <div className="bg-gray-50 min-h-screen pt-14 sm:pt-16">
      <div className="container mx-auto max-w-5xl px-4 py-4 sm:py-6" style={{ fontSize: isMobile() ? '0.9rem' : '1rem' }}>
        {/* Общая карточка для всего содержимого */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate("/people", { state: { activeTab: "guests" } })}
              className="flex items-center mr-4 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-1" />
              <span>Back</span>
            </button>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800 truncate max-w-[70%]">
              {listName || "Guest List"}
            </h1>
          </div>
          
          {/* Информационные карточки - Desktop view */}
          <div className="hidden sm:grid sm:grid-cols-3 gap-4 mb-6">
            <div className="p-4 rounded-lg  bg-indigo-50">
              <p className="text-sm text-gray-600 mb-1">Total Guests</p>
              <p className="text-2xl font-bold text-gray-800">{permanentGuests.length}</p>
            </div>
            
            <div className="p-4 rounded-lg  bg-indigo-50">
              <p className="text-sm text-gray-600 mb-1">Is Default</p>
              <p className="text-2xl font-bold text-gray-800">
                {listDetails?.is_default ? "Yes" : "No"}
              </p>
            </div>
            
            <div className="p-4 rounded-lg  bg-indigo-50">
              <p className="text-sm text-gray-600 mb-1">Total Plus</p>
              <p className="text-2xl font-bold text-gray-800">
                {permanentGuests.reduce((sum, guest) => sum + (guest.plus || 0), 0)}
              </p>
            </div>
          </div>

          {/* Mobile view - small square cards */}
          <div className="flex justify-evenly mb-4 gap-0 sm:hidden">
            <div className="py-2 rounded-lg  bg-indigo-50 w-[30%] flex flex-col justify-center px-1">
              <p className="text-[10px] text-gray-600 m-0 text-center">Total Guests</p>
              <p className="text-lg font-bold text-gray-800 text-center m-0">
                {permanentGuests.length}
              </p>
            </div>

            <div className="py-2 rounded-lg  bg-indigo-50 w-[30%] flex flex-col justify-center px-1">
              <p className="text-[10px] text-gray-600 m-0 text-center">Default</p>
              <p className="text-lg font-bold text-gray-800 text-center m-0">
                {listDetails?.is_default ? "Yes" : "No"}
              </p>
            </div>

            <div className="py-2 rounded-lg  bg-indigo-50 w-[30%] flex flex-col justify-center px-1">
              <p className="text-[10px] text-gray-600 m-0 text-center">Total Plus</p>
              <p className="text-lg font-bold text-gray-800 text-center m-0">
                {permanentGuests.reduce((sum, guest) => sum + (guest.plus || 0), 0)}
              </p>
            </div>
          </div>
          
          {/* Действия */}
          <div className="flex flex-col md:flex-row justify-between space-y-4 md:space-y-0 mb-6">
            <div className="flex space-x-2">
              <button
                onClick={handleAddGuestList}
                className="bg-indigo-600 text-white font-bold px-4 py-2 rounded flex items-center hover:bg-indigo-700 transition duration-200"
              >
                <PlusIcon className="mr-2 h-5 w-5" />
                Add Guest
              </button>
              
              {selectedGuestIds.length > 0 && (
                <button
                  onClick={() => setShowBulkConfirmModalGuests(true)}
                  className="flex items-center bg-white text-gray-600 px-4 py-2 rounded hover:bg-gray-50 border border-gray-300 transition duration-200"
                >
                  <TrashIcon className="h-5 w-5 mr-2" />
                   ({selectedGuestIds.length})
                </button>
              )}
            </div>
            
            {/* Поиск */}
            <div className="relative max-w-md w-full">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search guests..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Фильтры */}
          <div className="flex flex-wrap gap-2 justify-between items-center mb-4">
            {/* Текущие активные фильтры */}
            <div className="flex items-center gap-2">
              {viaFilter ? (
                <div className="flex items-center bg-indigo-50 text-indigo-700 px-3 py-2 rounded-md border border-indigo-200 text-sm">
                  <span>Via: <strong>{viaFilter}</strong></span>
                  <button 
                    onClick={clearViaFilter}
                    className="ml-2 text-indigo-500 hover:text-indigo-700"
                  >
                    <span>×</span>
                  </button>
                </div>
              ) : null}
              
              {categoryFilter ? (
                <div className="flex items-center bg-indigo-50 text-indigo-700 px-3 py-2 rounded-md border border-indigo-200 text-sm">
                  <span>Category: <strong>{categoryFilter}</strong></span>
                  <button 
                    onClick={clearCategoryFilter}
                    className="ml-2 text-indigo-500 hover:text-indigo-700"
                  >
                    <span>×</span>
                  </button>
                </div>
              ) : null}
            </div>

            {/* Кнопки фильтров */}
            <div className="flex items-center gap-2 relative">
              {/* Category Filter */}
              <div ref={categoryDropdownRef}>
                <button
                  onClick={() => setShowCategoryDropdown(prev => !prev)}
                  className="flex items-center bg-white text-gray-700 px-3 py-2 rounded-md hover:bg-gray-50 border border-gray-300 transition duration-200 text-sm"
                >
                  <span>Category Filter</span>
                  <span className="ml-1">{showCategoryDropdown ? '▲' : '▼'}</span>
                </button>
                
                {showCategoryDropdown && (
                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10 w-48 max-h-60 overflow-y-auto">
                    <ul className="py-1">
                      <li 
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm border-b border-gray-100"
                        onClick={clearCategoryFilter}
                      >
                        Show All
                      </li>
                      {getUniqueCategoryValues().map((category) => (
                        <li 
                          key={category} 
                          className={`px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm ${categoryFilter === category ? 'bg-indigo-50 text-indigo-700' : ''}`}
                          onClick={() => {
                            setCategoryFilter(category);
                            setShowCategoryDropdown(false);
                          }}
                        >
                          {category}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              
              {/* Via Filter */}
              <div ref={viaDropdownRef}>
                <button
                  onClick={() => setShowViaDropdown(prev => !prev)}
                  className="flex items-center bg-white text-gray-700 px-3 py-2 rounded-md hover:bg-gray-50 border border-gray-300 transition duration-200 text-sm"
                >
                  <span>Via Filter</span>
                  <span className="ml-1">{showViaDropdown ? '▲' : '▼'}</span>
                </button>
                
                {showViaDropdown && (
                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10 w-48 max-h-60 overflow-y-auto">
                    <ul className="py-1">
                      <li 
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm border-b border-gray-100"
                        onClick={clearViaFilter}
                      >
                        Show All
                      </li>
                      {getUniqueViaValues().map((via) => (
                        <li 
                          key={via} 
                          className={`px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm ${viaFilter === via ? 'bg-indigo-50 text-indigo-700' : ''}`}
                          onClick={() => {
                            setViaFilter(via);
                            setShowViaDropdown(false);
                          }}
                        >
                          {via}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Таблица гостей - в общей карточке без дополнительных теней */}
          <div className="mt-6">
            <div className="grid grid-cols-[40px_1fr_4fr_2fr_auto] items-center bg-gray-100 rounded-t-lg px-4 py-2">
              <div>
                <input
                  type="checkbox"
                  style={{ marginLeft: "5px" }}
                  checked={selectedGuestIds.length === sortedGuests.length && sortedGuests.length > 0}
                  onChange={handleSelectAllGuests}
                />
              </div>
              <h3 className="font-semibold text-gray-600 whitespace-nowrap" style={{ fontSize: isMobile() ? '1rem' : '1.125rem' }}>
                Guests
              </h3>
              <div></div>
              <div></div>
              <div className="flex justify-center">
                <button
                  onClick={() => setShowGuestList((prev) => !prev)}
                  className="focus:outline-none"
                >
                  {showGuestList ? (
                    <FiChevronUp className="text-xl" />
                  ) : (
                    <FiChevronDown className="text-xl" />
                  )}
                </button>
              </div>
            </div>

            {showGuestList && (
              <>
                {/* Desktop table header */}
                <div className="hidden sm:grid sm:grid-cols-[40px_minmax(150px,2fr)_0.5fr_minmax(100px,1.5fr)_minmax(100px,1.5fr)_minmax(100px,1fr)_80px] items-center border-b border-gray-200 py-1 px-4" style={{ fontSize: isMobile() ? '0.8rem' : '0.9rem' }}>
                  <div></div>
                  <div
                    className="flex items-center cursor-pointer"
                    onClick={() => handleSort("name")}
                  >
                    <span>Name</span>
                    <span className="ml-1">{renderSortIcon("name")}</span>
                  </div>
                  <div
                    className="flex items-center justify-center cursor-pointer"
                    onClick={() => handleSort("plus")}
                  >
                    <span>Plus</span>
                    <span className="ml-1">{renderSortIcon("plus")}</span>
                  </div>
                  <div
                    className="flex items-center justify-center cursor-pointer"
                    onClick={() => handleSort("via")}
                  >
                    <span>Via</span>
                    <span className="ml-1">{renderSortIcon("via")}</span>
                  </div>
                  <div
                    className="flex items-center justify-center cursor-pointer"
                    onClick={() => handleSort("admission_type")}
                  >
                    <span>Admission</span>
                    <span className="ml-1">{renderSortIcon("admission_type")}</span>
                  </div>
                  <div
                    className="flex items-center justify-center cursor-pointer"
                    onClick={() => handleSort("category")}
                  >
                    <span>Category</span>
                    <span className="ml-1">{renderSortIcon("category")}</span>
                  </div>
                  <div className="text-center">Actions</div>
                </div>
                
                {/* Mobile header with sorting */}
                <div className="sm:hidden flex justify-between items-center py-1.5 px-3 border-b border-gray-200 bg-gray-50">
                  <div className="flex space-x-4 items-center">
                    <div
                      className="text-sm font-medium cursor-pointer"
                      onClick={() => handleSort("name")}
                    >
                      Name {renderSortIcon("name")}
                    </div>
                    <div
                      className="text-sm font-medium cursor-pointer"
                      onClick={() => handleSort("category")}
                    >
                      Cat {renderSortIcon("category")}
                    </div>
                    <div
                      className="text-sm font-medium cursor-pointer"
                      onClick={() => handleSort("via")}
                    >
                      Via {renderSortIcon("via")}
                    </div>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      checked={
                        selectedGuestIds.length === sortedGuests.length &&
                        sortedGuests.length > 0
                      }
                      onChange={handleSelectAllGuests}
                    />
                  </div>
                </div>
                
                {loading ? (
                  <div className="py-20 flex justify-center items-center">
                    <Spinner />
                  </div>
                ) : sortedGuests.length === 0 ? (
                  <div className="py-10 text-center text-gray-500">
                    {searchQuery ? "No guests match your search." : "No guests available in this list."}
                  </div>
                ) : (
                  <div>
                    {sortedGuests.map((guest) => (
                      <div
                        key={guest.id}
                        className={`
                          sm:grid sm:grid-cols-[40px_minmax(150px,2fr)_0.5fr_minmax(100px,1.5fr)_minmax(100px,1.5fr)_minmax(100px,1fr)_80px]
                          flex flex-col
                          border-b border-indigo-100 hover:bg-indigo-50 transition duration-200 cursor-default bg-indigo-50
                        `}
                      >
                        {/* Mobile layout */}
                        <div className="sm:hidden w-full py-1 px-3 flex justify-between items-start">
                          <div className="flex-1">
                            <div className="font-medium leading-tight text-gray-900 flex items-center">
                              {renderCategoryIcon(guest.category)}
                              <span className="ml-1">{guest.name}</span>
                            </div>
                            <div className="text-xs text-gray-500 leading-tight">
                              Plus: {guest.plus || 0} | Via: {guest.via || "N/A"} | {guest.admission_type || "Free"} | {guest.category || "REGULAR"}
                            </div>
                          </div>
                          <div className="flex space-x-2 items-center">
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                              checked={selectedGuestIds.includes(guest.id)}
                              onChange={() => handleCheckboxChangeGuest(guest.id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditGuest(guest);
                              }}
                              className="text-gray-500 hover:text-indigo-600"
                            >
                              <PencilSquareIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(guest.id);
                              }}
                              className="text-gray-500 hover:text-red-600"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>

                        {/* Desktop layout */}
                        <div className="hidden sm:flex sm:items-center sm:justify-center">
                          <input
                            type="checkbox"
                            className="mx-auto"
                            checked={selectedGuestIds.includes(guest.id)}
                            onChange={() => handleCheckboxChangeGuest(guest.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <div className="hidden sm:flex text-base font-medium text-black items-center px-2 overflow-hidden whitespace-nowrap text-ellipsis" style={{ fontSize: isMobile() ? '0.9rem' : '1rem' }}>
                          {renderCategoryIcon(guest.category)}
                          <span className="ml-1">{guest.name}</span>
                        </div>
                        <div className="hidden sm:block text-sm font-medium text-gray-600 text-center px-2">
                          {guest.plus || 0}
                        </div>
                        <div className="hidden sm:block text-sm font-medium text-gray-600 text-center px-2 overflow-hidden whitespace-nowrap text-ellipsis">
                          {guest.via || "N/A"}
                        </div>
                        <div className="hidden sm:block text-sm font-medium text-gray-600 text-center px-2 overflow-hidden whitespace-nowrap text-ellipsis">
                          {guest.admission_type || "Free"}
                        </div>
                        <div className="hidden sm:block text-sm font-medium text-gray-600 text-center px-2 overflow-hidden whitespace-nowrap text-ellipsis">
                          {guest.category || "REGULAR"}
                        </div>
                        <div className="hidden sm:flex sm:space-x-2 sm:justify-center sm:px-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditGuest(guest);
                            }}
                            className="text-gray-500 hover:text-yellow-700"
                          >
                            <PencilSquareIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(guest.id);
                            }}
                            className="text-gray-500 hover:text-red-600"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Пустое состояние, когда нет гостей */}
          {!loading && showGuestList && sortedGuests.length === 0 && !searchQuery && (
            <div className="p-8 sm:p-12 text-center border rounded-lg my-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No guests found</h3>
              <p className="text-gray-500 mb-6">Add your first guest to get started.</p>
              <button
                onClick={handleAddGuestList}
                className="bg-indigo-600 text-white font-bold px-4 py-2 rounded hover:bg-indigo-700 transition duration-200"
              >
                <PlusIcon className="inline-block h-5 w-5 mr-2" />
                Add Guest
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleDeleteGuest}
        message="Are you sure you want to delete this permanent guest?"
      />

      {showAddModal && (
        <AddPermanentGuestModalForList
          isOpen={showAddModal}
          onClose={handleCloseModal}
          onSave={handleCreateGuest}
          currentUserName={user?.name || "N/A"}
        />
      )}

      {showEditModal && guestToEdit && (
        <EditPermanentGuestModalForList
          isOpen={showEditModal}
          onClose={handleCloseEditModal}
          onSave={handleUpdateGuest}
          guestData={guestToEdit}
        />
      )}

      <ConfirmModal
        isOpen={showBulkConfirmModalGuests}
        onClose={() => setShowBulkConfirmModalGuests(false)}
        onConfirm={confirmBulkDeleteGuests}
        message="Are you sure you want to delete the selected permanent guests?"
      />
    </div>
  );
}

export default PermanentGuestListPage;